import { Logtail } from '@logtail/browser';
import { ErrorEvent, EventHint } from '@sentry/types';
import { BROWSER_SESSION_ID, NONCE } from './browserSession';
import { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { Operation } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql/error';
import { getAllExperiments } from './App.constants';
import { featureFlagsState } from './feature-flags';
import { LOG_LOGGER_KEY } from './debug-mode/constants';
import { safeConsole } from './safeConsole';
import { envConfig } from './constants/envConfig';

// export const LOGGER_ENABLED = true; // 👈 Use for testing
export const LOGGER_ENABLED = envConfig.MODE === 'production';

let dailySessionId: string | undefined;
export const setDailySessionId = (id: string | undefined) => {
    dailySessionId = id;
};

const shouldLogToConsole = () =>
    localStorage.getItem(LOG_LOGGER_KEY) === 'true';

const logger = new Logtail(envConfig.VITE_BETTERSTACK_SOURCE_TOKEN, {
    endpoint: envConfig.VITE_BETTERSTACK_ENDPOINT,
    sendLogsToBetterStack: LOGGER_ENABLED,
    // sendLogsToConsoleOutput: true, // 👈 Use for testing
});

logger.use(async (log) => {
    return {
        ...log,
        browserSession: BROWSER_SESSION_ID,
        nonce: NONCE,
        ...(dailySessionId && { dailySessionId }),
        // Compatibility with Sentry logs, we might change this in future
        tags: {
            ...log.tags,
            browser_session: BROWSER_SESSION_ID,
        },
        experiments: getAllExperiments(),
        featureFlagsState,
    };
});

logger.use(async (log) => {
    if (shouldLogToConsole()) {
        safeConsole.__dangerouslyAllowedInProduction.info(
            `trackLogger: ${log.message}`,
            log,
        );
    }

    return log;
});

window?.addEventListener('offline', (event) => {
    logger.warn('[network] offline', { event });
});

window?.addEventListener('online', (event) => {
    logger.warn('[network] online', { event });
});

export const sendSentryEventsAsLogs = (event: ErrorEvent, hint: EventHint) => {
    // Ignore non-error events
    if (event.type !== undefined) {
        return;
    }

    const name =
        event.message ||
        event.exception?.values?.[0]?.value ||
        (hint.originalException as Error)?.message ||
        hint.syntheticException?.message ||
        'Sentry event occurred';

    // Ignore Sentry replay hack
    if (name === 'startSentryReplay') {
        return;
    }

    const {
        breadcrumbs: _omit1,
        contexts: _omit2,
        sdk: _omit3,
        sdkProcessingMetadata: _omit4,
        ...data
    } = event;

    try {
        void logger.log(name, event.level, data);
    } catch (error) {
        // ignore
    }
};

const getMetadataFromAxiosConfig = (config: InternalAxiosRequestConfig) => {
    const method = config.method?.toUpperCase() ?? '?';
    const search = new URLSearchParams(config.params).toString();

    return {
        title: `${method} ${config.url}${search ? '?' : ''}${search}`,
        correlationId: config.headers['correlation-id'],
        method,
        baseURL: config.baseURL,
        url: config.url ?? 'undefined',
        params: config.params,
    };
};

export const sendAxiosRequestAsLog = (config: InternalAxiosRequestConfig) => {
    try {
        const { title, ...rest } = getMetadataFromAxiosConfig(config);

        logger.info(`[api.request] ${title}`, {
            ...rest,
        });
    } catch {
        // ignore
    }
};

export const sendAxiosResponseAsLog = (
    config?: InternalAxiosRequestConfig,
    response?: AxiosResponse,
    error?: any,
) => {
    if (!config) {
        return;
    }

    try {
        const { title, ...rest } = getMetadataFromAxiosConfig(config);

        const responseStatus = response?.status || 0;

        logger.info(`[api.response:${responseStatus}] ${title}`, {
            ...rest,
            status: response?.status,
            bodySize: response?.request?.response?.length,
            ...(error && {
                error: {
                    name: error.name,
                    code: error.code,
                    message: error.message,
                    stack: error.stack,
                },
            }),
        });
    } catch {
        // ignore
    }
};

const getMetadataFromApolloOperation = (operation: Operation) => {
    const context = operation.getContext();

    return {
        operationName: operation.operationName,
        correlationId: context.headers?.['correlation-id'],
        status: context.response?.status,
    };
};

export const sendApolloRequestAsLog = (operation: Operation) => {
    try {
        const { operationName, ...rest } =
            getMetadataFromApolloOperation(operation);

        logger.info(`[graphql.request] ${operationName}`, rest);
    } catch {
        // ignore
    }
};
export const sendApolloResponseAsLog = (
    operation: Operation,
    graphQLErrors?: ReadonlyArray<GraphQLFormattedError>,
) => {
    try {
        const { operationName, status, ...rest } =
            getMetadataFromApolloOperation(operation);
        const errorTitleSuffix = graphQLErrors ? ':with-errors' : '';

        logger.info(
            `[graphql.response:${status}${errorTitleSuffix}] ${operationName}`,
            {
                ...rest,
                graphQLErrors,
            },
        );
    } catch {
        // ignore
    }
};

export default logger;
